/**
 * ProjectClusterLayer — Phase 3
 *
 * MapLibre native clustering for project points.
 * Replaces manual 3-level clustering (region→city→individual) with
 * MapLibre's built-in Supercluster-based source clustering.
 *
 * Responsibilities:
 * - Fetch project + reference data from existing API endpoints
 * - Apply filters (region, city, district, category, subcategory, search)
 * - Render clusters as sized circles with count labels
 * - Render individual points as category-colored circles
 * - Report visible project count
 * - Delegate clicks to useMapInteractions hook
 */
import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import { useLanguage } from '../../../contexts/LanguageContext';
import { ZOOM, CLUSTER, CAMERA } from '../interactions/constants';
import saudiGeoData from '../../../data/saudi_regions_simplified.json';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Category color palette (same as old ProjectsLayer) ── */
const CATEGORY_COLORS = [
  { fill: '#FF6B6B', stroke: '#FF4040', glow: 'rgba(255,107,107,0.35)' },
  { fill: '#4ECB71', stroke: '#2EAB51', glow: 'rgba(78,203,113,0.35)' },
  { fill: '#4ECDC4', stroke: '#2EADA4', glow: 'rgba(78,205,196,0.35)' },
  { fill: '#F7DC6F', stroke: '#D7BC4F', glow: 'rgba(247,220,111,0.35)' },
  { fill: '#BB8FCE', stroke: '#9B6FAE', glow: 'rgba(187,143,206,0.35)' },
  { fill: '#5DADE2', stroke: '#3D8DC2', glow: 'rgba(93,173,226,0.35)' },
  { fill: '#F1948A', stroke: '#D1746A', glow: 'rgba(241,148,138,0.35)' },
  { fill: '#F0B27A', stroke: '#D0925A', glow: 'rgba(240,178,122,0.35)' },
  { fill: '#AEB6BF', stroke: '#8E969F', glow: 'rgba(174,182,191,0.35)' },
];

const categoryColorCache = {};
let colorIdx = 0;
function getCategoryColor(categoryId) {
  if (!categoryId) return CATEGORY_COLORS[CATEGORY_COLORS.length - 1];
  if (!categoryColorCache[categoryId]) {
    categoryColorCache[categoryId] = CATEGORY_COLORS[colorIdx % CATEGORY_COLORS.length];
    colorIdx++;
  }
  return categoryColorCache[categoryId];
}

function stripPrefix(name) {
  if (!name) return name;
  return name.replace(/^(منطقة |المنطقة |Region )/, '');
}

/* ── Primary City Hubs — the foundation of Level-1 clustering ── */
const PRIMARY_HUBS = [
  // Riyadh Region
  { name: 'الرياض', lng: 46.7167, lat: 24.6333, region: 'الرياض' },
  { name: 'الدرعية', lng: 46.5728, lat: 24.7336, region: 'الرياض' },
  { name: 'الخرج', lng: 47.3125, lat: 24.1556, region: 'الرياض' },
  { name: 'المجمعة', lng: 45.3667, lat: 25.9167, region: 'الرياض' },
  { name: 'القويعية', lng: 45.2667, lat: 24.0667, region: 'الرياض' },
  { name: 'الزلفي', lng: 44.8333, lat: 26.3000, region: 'الرياض' },
  { name: 'شقراء', lng: 45.2500, lat: 25.2500, region: 'الرياض' },
  { name: 'وادي الدواسر', lng: 44.7167, lat: 20.4667, region: 'الرياض' },
  { name: 'الأفلاج', lng: 46.7333, lat: 22.3167, region: 'الرياض' },
  // Makkah Region
  { name: 'جدة', lng: 39.1728, lat: 21.5433, region: 'مكة' },
  { name: 'مكة المكرمة', lng: 39.8261, lat: 21.4225, region: 'مكة' },
  { name: 'الطائف', lng: 40.4167, lat: 21.2703, region: 'مكة' },
  { name: 'رابغ', lng: 39.0167, lat: 22.7917, region: 'مكة' },
  { name: 'القنفذة', lng: 41.0778, lat: 19.1306, region: 'مكة' },
  // Eastern Region
  { name: 'الدمام', lng: 50.1033, lat: 26.4367, region: 'الشرقية' },
  { name: 'الخبر', lng: 50.2083, lat: 26.2794, region: 'الشرقية' },
  { name: 'الظهران', lng: 50.1500, lat: 26.2667, region: 'الشرقية' },
  { name: 'الأحساء', lng: 49.5856, lat: 25.3833, region: 'الشرقية' },
  { name: 'الجبيل', lng: 49.6225, lat: 27.0046, region: 'الشرقية' },
  { name: 'القطيف', lng: 50.0133, lat: 26.5194, region: 'الشرقية' },
  // Madinah Region
  { name: 'المدينة المنورة', lng: 39.6142, lat: 24.4672, region: 'المدينة' },
  { name: 'ينبع', lng: 38.0631, lat: 24.0895, region: 'المدينة' },
  { name: 'العلا', lng: 37.9208, lat: 26.6167, region: 'المدينة' },
  // Qassim Region
  { name: 'بريدة', lng: 43.9750, lat: 26.3267, region: 'القصيم' },
  { name: 'عنيزة', lng: 43.9833, lat: 26.0833, region: 'القصيم' },
  { name: 'الرس', lng: 43.5000, lat: 25.8667, region: 'القصيم' },
  { name: 'البكيرية', lng: 43.6667, lat: 26.1500, region: 'القصيم' },
  // Asir Region
  { name: 'أبها', lng: 42.5053, lat: 18.2164, region: 'عسير' },
  { name: 'خميس مشيط', lng: 42.7333, lat: 18.3000, region: 'عسير' },
  { name: 'بيشة', lng: 42.5833, lat: 20.0000, region: 'عسير' },
  { name: 'النماص', lng: 42.1333, lat: 19.1167, region: 'عسير' },
  // Tabuk Region
  { name: 'تبوك', lng: 36.5714, lat: 28.3836, region: 'تبوك' },
  { name: 'ضباء', lng: 36.5000, lat: 27.3500, region: 'تبوك' },
  { name: 'الوجه', lng: 36.4500, lat: 26.2333, region: 'تبوك' },
  // Hail Region
  { name: 'حائل', lng: 41.6908, lat: 27.5114, region: 'حائل' },
  { name: 'بقعاء', lng: 42.0667, lat: 27.9167, region: 'حائل' },
  // Jawf Region
  { name: 'سكاكا', lng: 40.2064, lat: 29.9697, region: 'الجوف' },
  { name: 'القريات', lng: 37.3500, lat: 31.3333, region: 'الجوف' },
  { name: 'دومة الجندل', lng: 39.8667, lat: 29.8167, region: 'الجوف' },
  // Northern Borders Region
  { name: 'عرعر', lng: 41.0236, lat: 30.9753, region: 'الحدود الشمالية' },
  { name: 'رفحاء', lng: 43.4833, lat: 29.6167, region: 'الحدود الشمالية' },
  { name: 'طريف', lng: 38.6667, lat: 31.6667, region: 'الحدود الشمالية' },
  // Jazan Region
  { name: 'جازان', lng: 42.5611, lat: 16.8892, region: 'جازان' },
  { name: 'صبيا', lng: 42.6333, lat: 17.1500, region: 'جازان' },
  { name: 'أبو عريش', lng: 42.8000, lat: 16.9667, region: 'جازان' },
  // Najran Region
  { name: 'نجران', lng: 44.1322, lat: 17.4917, region: 'نجران' },
  { name: 'شرورة', lng: 47.1167, lat: 17.4667, region: 'نجران' },
  // Bahah Region
  { name: 'الباحة', lng: 41.4683, lat: 20.0000, region: 'الباحة' },
  { name: 'بلجرشي', lng: 41.5833, lat: 19.8500, region: 'الباحة' },
  { name: 'المخواة', lng: 41.4333, lat: 19.7667, region: 'الباحة' },
];

/* ════════════════════════════════════════════════════════
 * Component
 * ════════════════════════════════════════════════════════ */
export default function ProjectClusterLayer({
  map,
  filters = {},
  layers = {},
  onResultCount,
  onProjectSelect,
  theme = 'dark',
  presentationMode = false,
}) {
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  const isLight = theme === 'light';

  /* ── Reference data ── */
  const [projects, setProjects] = useState([]);
  const [successStories, setSuccessStories] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [jadaBranches, setJadaBranches] = useState([]);
  const [cities, setCities] = useState([]);
  const [regions, setRegions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const layersAddedRef = useRef(false);
  const storyLayerAddedRef = useRef(false);
  const outletsLayerAddedRef = useRef(false);
  const jadaLayerAddedRef = useRef(false);
  const popupRef = useRef(null);
  const presentationModeRef = useRef(false);
  presentationModeRef.current = presentationMode;

  /* ── Fetch data once ── */
  useEffect(() => {
    if (!API_URL) return;
    Promise.all([
      fetch(`${API_URL}/api/projects/map-data`).then(r => r.json()),
      fetch(`${API_URL}/api/cities`).then(r => r.json()),
      fetch(`${API_URL}/api/regions`).then(r => r.json()),
      fetch(`${API_URL}/api/categories`).then(r => r.json()),
      fetch(`${API_URL}/api/districts`).then(r => r.json()),
      fetch(`${API_URL}/api/subcategories`).then(r => r.json()),
      fetch(`${API_URL}/api/success-stories/map-data`).then(r => r.json()),
      fetch(`${API_URL}/api/outlets/map-data`).then(r => r.json()).catch(() => []),
      fetch(`${API_URL}/api/jada30/map-data`).then(r => r.json()).catch(() => []),
    ]).then(([proj, cit, reg, cat, dist, sub, stories, outl, jada]) => {
      setProjects(proj);
      setCities(cit);
      setRegions(reg);
      setCategories(cat);
      setDistricts(dist);
      setSubcategories(sub);
      setSuccessStories(stories || []);
      setOutlets(outl || []);
      setJadaBranches(jada || []);
      setLoaded(true);
    }).catch(err => console.error('ProjectClusterLayer fetch error:', err));
  }, []);

  /* ── Apply filters ── */
  const filteredProjects = useMemo(() => {
    let list = projects;
    if (!layers.projects) return [];
    if (filters.regionId) list = list.filter(p => p.region_id === filters.regionId);
    if (filters.cityId) list = list.filter(p => p.city_id === filters.cityId);
    if (filters.districtId) list = list.filter(p => p.district_id === filters.districtId);
    if (filters.categoryId) list = list.filter(p => p.category_id === filters.categoryId);
    if (filters.subcategoryId) list = list.filter(p => p.subcategory_id === filters.subcategoryId);
    if (filters.search) {
      const q = filters.search.toLowerCase();
      const cityNames = {};
      cities.forEach(c => { cityNames[c.id] = `${c.name_ar} ${c.name_en || ''}`.toLowerCase(); });
      const regionNames = {};
      regions.forEach(r => { regionNames[r.id] = `${r.name_ar} ${r.name_en || ''}`.toLowerCase(); });
      list = list.filter(p =>
        (p.project_name_ar || '').toLowerCase().includes(q) ||
        (p.project_name_en || '').toLowerCase().includes(q) ||
        (cityNames[p.city_id] || '').includes(q) ||
        (regionNames[p.region_id] || '').includes(q)
      );
    }
    return list;
  }, [projects, cities, regions, filters, layers.projects]);

  /* ── Report count ── */
  useEffect(() => {
    if (onResultCount) onResultCount(filteredProjects.length);
  }, [filteredProjects.length, onResultCount]);

  /* ── Convert to GeoJSON (with spiderfy for co-located points) ── */
  const geojson = useMemo(() => {
    // Exclude featured projects — they live in their own unclustered source
    const nonFeatured = filteredProjects.filter(p => !p.is_featured);

    // Group projects by coordinate key to detect overlaps
    const coordGroups = {};
    nonFeatured.forEach(p => {
      const key = `${Number(p.latitude).toFixed(5)}_${Number(p.longitude).toFixed(5)}`;
      if (!coordGroups[key]) coordGroups[key] = [];
      coordGroups[key].push(p);
    });

    const features = [];
    for (const group of Object.values(coordGroups)) {
      const count = group.length;
      group.forEach((p, idx) => {
        const catColor = getCategoryColor(p.category_id);
        let lng = p.longitude;
        let lat = p.latitude;

        // Spiderfy: offset co-located points into a ring
        if (count > 1) {
          const angle = (idx / count) * 2 * Math.PI;
          lng += CLUSTER.SPIDERFY_OFFSET * Math.cos(angle);
          lat += CLUSTER.SPIDERFY_OFFSET * Math.sin(angle);
        }

        // Category name for label
        const cat = categories.find(c => c.id === p.category_id);
        const catName = isArabic ? (cat?.name_ar || '') : (cat?.name_en || cat?.name_ar || '');

        features.push({
          type: 'Feature',
          properties: {
            id: p.id,
            project_name_ar: p.project_name_ar || '',
            project_name_en: p.project_name_en || '',
            category_id: p.category_id || '',
            category_name: catName,
            region_id: p.region_id || '',
            city_id: p.city_id || '',
            district_id: p.district_id || '',
            subcategory_id: p.subcategory_id || '',
            status: p.status || 'draft',
            is_featured: p.is_featured ? 1 : 0,
            color: catColor.fill,
            stroke: catColor.stroke,
            glow: catColor.glow,
            short_description_ar: p.short_description_ar || '',
            _overlapCount: count,
          },
          geometry: {
            type: 'Point',
            coordinates: [lng, lat],
          },
        });
      });
    }

    return { type: 'FeatureCollection', features };
  }, [filteredProjects, categories, isArabic]);

  /* ── Featured projects GeoJSON (separate, never clustered) ── */
  const featuredGeojson = useMemo(() => {
    const features = [];
    filteredProjects.forEach(p => {
      if (!p.is_featured || !p.longitude || !p.latitude) return;
      const catColor = getCategoryColor(p.category_id);
      const cat = categories.find(c => c.id === p.category_id);
      const catName = isArabic ? (cat?.name_ar || '') : (cat?.name_en || cat?.name_ar || '');
      features.push({
        type: 'Feature',
        properties: {
          id: p.id,
          project_name_ar: p.project_name_ar || '',
          project_name_en: p.project_name_en || '',
          category_id: p.category_id || '',
          category_name: catName,
          region_id: p.region_id || '',
          city_id: p.city_id || '',
          district_id: p.district_id || '',
          subcategory_id: p.subcategory_id || '',
          status: p.status || 'draft',
          is_featured: 1,
          color: catColor.fill,
          stroke: catColor.stroke,
          glow: catColor.glow,
          short_description_ar: p.short_description_ar || '',
        },
        geometry: { type: 'Point', coordinates: [p.longitude, p.latitude] },
      });
    });
    return { type: 'FeatureCollection', features };
  }, [filteredProjects, categories, isArabic]);

  /* ── Hub-level aggregation: assign every project to nearest Primary Hub ── */
  const hubClustersGeo = useMemo(() => {
    // Build city name → hub mapping
    const cityToHub = {};
    cities.forEach(city => {
      const cn = (city.name_ar || '').trim();
      // Direct match
      const match = PRIMARY_HUBS.find(h => cn === h.name || cn.includes(h.name) || h.name.includes(cn));
      if (match) { cityToHub[city.id] = match.name; return; }
      // Region-based nearest hub
      const region = regions.find(r => r.id === city.region_id);
      const rn = region?.name_ar || '';
      const regionHubs = PRIMARY_HUBS.filter(h => rn.includes(h.region) || h.region.includes(rn));
      if (regionHubs.length === 1) { cityToHub[city.id] = regionHubs[0].name; }
    });

    // Aggregate projects by hub
    const byHub = {};
    filteredProjects.forEach(p => {
      if (!p.longitude || !p.latitude) return;
      let hubName = cityToHub[p.city_id];
      if (!hubName) {
        let best = PRIMARY_HUBS[0], bestD = Infinity;
        const pLng = Number(p.longitude), pLat = Number(p.latitude);
        PRIMARY_HUBS.forEach(h => {
          const d = (pLng - h.lng) ** 2 + (pLat - h.lat) ** 2;
          if (d < bestD) { bestD = d; best = h; }
        });
        hubName = best.name;
        if (p.city_id) cityToHub[p.city_id] = hubName;
      }
      if (!byHub[hubName]) byHub[hubName] = { count: 0 };
      byHub[hubName].count++;
    });

    // ── Proximity merge: combine nearby hubs, but PROTECT biggest per region ──
    const PROXIMITY = 1.0; // ~1 degree ≈ overlapping circles at initial zoom
    const allHubsSorted = Object.entries(byHub)
      .map(([name, d]) => ({ name, count: d.count }))
      .sort((a, b) => b.count - a.count);

    // Protect the biggest hub in each region (never absorbed)
    const protectedHubs = new Set();
    const regionBiggest = {};
    allHubsSorted.forEach(h => {
      const hub = PRIMARY_HUBS.find(ph => ph.name === h.name);
      if (!hub) return;
      if (!regionBiggest[hub.region] || h.count > regionBiggest[hub.region].count) {
        regionBiggest[hub.region] = h;
      }
    });
    Object.values(regionBiggest).forEach(h => protectedHubs.add(h.name));

    // Absorb non-protected hubs that are too close to a larger hub
    const absorbed = new Set();
    allHubsSorted.forEach(big => {
      if (absorbed.has(big.name)) return;
      const bigHub = PRIMARY_HUBS.find(ph => ph.name === big.name);
      if (!bigHub) return;
      allHubsSorted.forEach(small => {
        if (small.name === big.name || absorbed.has(small.name) || protectedHubs.has(small.name)) return;
        const sHub = PRIMARY_HUBS.find(ph => ph.name === small.name);
        if (!sHub) return;
        const dist = Math.sqrt((bigHub.lng - sHub.lng) ** 2 + (bigHub.lat - sHub.lat) ** 2);
        if (dist < PROXIMITY) {
          byHub[big.name].count += small.count;
          delete byHub[small.name];
          absorbed.add(small.name);
        }
      });
    });

    const dataFeatures = Object.entries(byHub).map(([hubName, data]) => {
      const hub = PRIMARY_HUBS.find(h => h.name === hubName);
      if (!hub) return null;
      return {
        type: 'Feature',
        properties: {
          hub_name: hubName,
          count: data.count,
          name: hubName,
          is_regional: protectedHubs.has(hubName) ? 1 : 0,
        },
        geometry: { type: 'Point', coordinates: [hub.lng, hub.lat] },
      };
    }).filter(Boolean);

    // If no projects active, add core hubs as label-only anchors (count=0)
    if (dataFeatures.length === 0) {
      const CORE = ['الرياض','جدة','مكة المكرمة','المدينة المنورة','الدمام','تبوك','أبها','بريدة','حائل','جازان','نجران','الأحساء','الخبر','ينبع','الطائف','سكاكا','عرعر','الباحة'];
      CORE.forEach(n => {
        const hub = PRIMARY_HUBS.find(h => h.name === n);
        if (hub) dataFeatures.push({ type: 'Feature', properties: { hub_name: n, count: 0, name: n, is_regional: 1, label_only: 1 }, geometry: { type: 'Point', coordinates: [hub.lng, hub.lat] } });
      });
    }

    return { type: 'FeatureCollection', features: dataFeatures };
  }, [filteredProjects, cities, regions]);

  /* ── City-level aggregation for Level-2 clusters ── */
  const cityClustersGeo = useMemo(() => {
    const byCity = {};
    filteredProjects.forEach(p => {
      if (!p.city_id || !p.longitude || !p.latitude) return;
      if (!byCity[p.city_id]) byCity[p.city_id] = { count: 0, lngs: [], lats: [], region_id: p.region_id };
      const b = byCity[p.city_id];
      b.count++;
      b.lngs.push(Number(p.longitude));
      b.lats.push(Number(p.latitude));
    });

    const features = Object.entries(byCity).map(([cityId, data]) => {
      const city = cities.find(c => c.id === cityId);
      const cityName = isArabic ? (city?.name_ar || '') : (city?.name_en || city?.name_ar || '');
      return {
        type: 'Feature',
        properties: {
          city_id: cityId,
          count: data.count,
          name: cityName,
          minLng: Math.min(...data.lngs),
          maxLng: Math.max(...data.lngs),
          minLat: Math.min(...data.lats),
          maxLat: Math.max(...data.lats),
        },
        geometry: {
          type: 'Point',
          coordinates: [
            data.lngs.reduce((a, b) => a + b, 0) / data.count,
            data.lats.reduce((a, b) => a + b, 0) / data.count,
          ],
        },
      };
    });

    return { type: 'FeatureCollection', features };
  }, [filteredProjects, cities, isArabic]);

  /* ── District-level aggregation for Level-3 clusters ── */
  const districtClustersGeo = useMemo(() => {
    const byDistrict = {};
    filteredProjects.forEach(p => {
      const key = p.district_id || `city_${p.city_id}_nodistrict`;
      if (!p.longitude || !p.latitude) return;
      if (!byDistrict[key]) byDistrict[key] = { count: 0, lngs: [], lats: [], city_id: p.city_id, district_id: p.district_id };
      const b = byDistrict[key];
      b.count++;
      b.lngs.push(Number(p.longitude));
      b.lats.push(Number(p.latitude));
    });

    const features = Object.entries(byDistrict).map(([key, data]) => {
      let distName = '';
      if (data.district_id) {
        const dist = districts.find(d => d.id === data.district_id);
        distName = isArabic ? (dist?.name_ar || '') : (dist?.name_en || dist?.name_ar || '');
      }
      if (!distName) {
        const city = cities.find(c => c.id === data.city_id);
        distName = isArabic ? (city?.name_ar || '') : (city?.name_en || city?.name_ar || '');
      }
      return {
        type: 'Feature',
        properties: {
          district_key: key,
          count: data.count,
          name: distName,
          minLng: Math.min(...data.lngs),
          maxLng: Math.max(...data.lngs),
          minLat: Math.min(...data.lats),
          maxLat: Math.max(...data.lats),
        },
        geometry: {
          type: 'Point',
          coordinates: [
            data.lngs.reduce((a, b) => a + b, 0) / data.count,
            data.lats.reduce((a, b) => a + b, 0) / data.count,
          ],
        },
      };
    });

    return { type: 'FeatureCollection', features };
  }, [filteredProjects, districts, cities, isArabic]);

  /* ── Enrich project data for preview panel ── */
  const enrichProject = useCallback((props, coords) => {
    const city = cities.find(c => c.id === props.category_id ? false : c.id === props.city_id);
    const region = regions.find(r => r.id === props.region_id);
    const category = categories.find(c => c.id === props.category_id);
    const district = districts.find(d => d.id === props.district_id);
    const subcategory = subcategories.find(s => s.id === props.subcategory_id);
    const catColor = getCategoryColor(props.category_id);
    const cityObj = cities.find(c => c.id === props.city_id);

    return {
      id: props.id,
      project_name_ar: props.project_name_ar,
      project_name_en: props.project_name_en,
      status: props.status,
      is_featured: props.is_featured === 1,
      latitude: coords[1],
      longitude: coords[0],
      cityName: isArabic ? cityObj?.name_ar : (cityObj?.name_en || cityObj?.name_ar),
      regionName: stripPrefix(isArabic ? region?.name_ar : (region?.name_en || region?.name_ar)),
      categoryName: isArabic ? category?.name_ar : (category?.name_en || category?.name_ar),
      categoryColor: catColor.fill,
      districtName: isArabic ? district?.name_ar : (district?.name_en || district?.name_ar),
      subcategoryName: isArabic ? subcategory?.name_ar : (subcategory?.name_en || subcategory?.name_ar),
      short_description: props.short_description_ar || '',
    };
  }, [cities, regions, categories, districts, subcategories, isArabic]);

  /* ── Success Stories GeoJSON ── */
  const storiesGeojson = useMemo(() => {
    if (!layers.successStories) return { type: 'FeatureCollection', features: [] };
    const features = successStories
      .filter(s => s.latitude && s.longitude && s.is_active)
      .map(s => ({
        type: 'Feature',
        properties: {
          id: s.id,
          title_ar: s.title_ar || '',
          title_en: s.title_en || '',
          city: s.city || '',
          region: s.region || '',
          sector: s.sector || '',
          short_hook: s.short_hook || '',
          cover_image: s.cover_image || '',
          _type: 'success_story',
        },
        geometry: { type: 'Point', coordinates: [s.longitude, s.latitude] },
      }));
    return { type: 'FeatureCollection', features };
  }, [successStories, layers.successStories]);

  /* ── Outlets GeoJSON ── */
  const outletsGeojson = useMemo(() => {
    if (!layers.outlets) return { type: 'FeatureCollection', features: [] };
    return { type: 'FeatureCollection', features: outlets.filter(o => o.latitude && o.longitude).map(o => ({
      type: 'Feature',
      properties: { id: o.id, name: o.outlet_name_ar || '', category: o.outlet_category || '', _type: 'outlet' },
      geometry: { type: 'Point', coordinates: [o.longitude, o.latitude] },
    }))};
  }, [outlets, layers.outlets]);

  /* ── Jada30 GeoJSON ── */
  const jadaGeojson = useMemo(() => {
    if (!layers.jada30) return { type: 'FeatureCollection', features: [] };
    return { type: 'FeatureCollection', features: jadaBranches.filter(b => b.latitude && b.longitude).map(b => ({
      type: 'Feature',
      properties: { id: b.id, name: b.branch_name_ar || '', type: b.branch_type || '', services: (b.services || []).join('، '), _type: 'jada30' },
      geometry: { type: 'Point', coordinates: [b.longitude, b.latitude] },
    }))};
  }, [jadaBranches, layers.jada30]);

  /* ── Safe helpers — prevent "already exists" errors ── */
  const safeSource = useCallback((id, opts) => {
    if (map && !map.getSource(id)) map.addSource(id, opts);
  }, [map]);
  const safeLayer = useCallback((layer) => {
    if (map) {
      try { if (map.getLayer(layer.id)) map.removeLayer(layer.id); } catch (_) {}
      map.addLayer(layer);
    }
  }, [map]);

  /* ── Add MapLibre layers ── */
  useEffect(() => {
    if (!map || !loaded || layersAddedRef.current) return;
    if (map.getSource('projects')) return;
    layersAddedRef.current = true;
    window.__mapV2 = map;

    // Source: clustered projects
    safeSource('projects', {
      type: 'geojson',
      data: geojson,
      cluster: true,
      clusterRadius: CLUSTER.RADIUS,
      clusterMaxZoom: CLUSTER.MAX_ZOOM,
    });

    /* ═══ CLUSTER LAYERS ═══ */

    /* ─── Cluster circle — solid green (hidden at low zoom where region clusters show) ─── */
    safeLayer({
      id: 'project-clusters',
      type: 'circle',
      source: 'projects',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': 'rgba(47, 174, 143, 0.85)',
        'circle-radius': ['step', ['get', 'point_count'],
          14, 10, 17, 30, 20, 50, 24,
        ],
        'circle-stroke-width': 1.5,
        'circle-stroke-color': 'rgba(47, 174, 143, 0.4)',
        'circle-opacity': ['interpolate', ['linear'], ['zoom'], 10, 0, 11, 1],
        'circle-stroke-opacity': ['interpolate', ['linear'], ['zoom'], 10, 0, 11, 1],
      },
    });

    /* ─── Cluster click hitarea (invisible, EXTRA LARGE for touch screens) ─── */
    safeLayer({
      id: 'project-clusters-hitarea',
      type: 'circle',
      source: 'projects',
      filter: ['has', 'point_count'],
      minzoom: 10,
      paint: {
        'circle-color': 'rgba(0, 0, 0, 0.01)',
        'circle-radius': ['step', ['get', 'point_count'],
          50, 10, 56, 30, 62, 50, 70,
        ],
      },
    });

    /* ═══ INDIVIDUAL PROJECT LAYERS — ENHANCED WITH PULSE & GLOW ═══ */

    /* ─── 1. Outer glow — soft category-colored halo ─── */
    safeLayer({
      id: 'project-points-glow',
      type: 'circle',
      source: 'projects',
      filter: ['!', ['has', 'point_count']],
      minzoom: 11,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'],
          ZOOM.CITY_MAX,    ['case', ['==', ['get', 'is_featured'], 1], 14, 10],
          ZOOM.DENSE_MIN,   ['case', ['==', ['get', 'is_featured'], 1], 18, 14],
          ZOOM.PROJECT_MIN, ['case', ['==', ['get', 'is_featured'], 1], 24, 18],
          ZOOM.LOCAL_MIN,   ['case', ['==', ['get', 'is_featured'], 1], 30, 24],
        ],
        'circle-color': ['get', 'color'],
        'circle-opacity': ['interpolate', ['linear'], ['zoom'],
          ZOOM.CITY_MIN, 0,
          ZOOM.CITY_MAX, 0.12,
          ZOOM.DENSE_MIN, 0.18,
        ],
        'circle-blur': 0.8,
      },
    });

    /* ─── 2. Pulse ring — animated via JS ─── */
    safeLayer({
      id: 'project-points-pulse',
      type: 'circle',
      source: 'projects',
      filter: ['!', ['has', 'point_count']],
      minzoom: 11,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'],
          ZOOM.CITY_MAX, 8,
          ZOOM.PROJECT_MIN, 14,
          ZOOM.LOCAL_MIN, 18,
        ],
        'circle-color': 'transparent',
        'circle-stroke-width': ['interpolate', ['linear'], ['zoom'],
          ZOOM.CITY_MAX, 1.5,
          ZOOM.PROJECT_MIN, 2,
          ZOOM.LOCAL_MIN, 2.5,
        ],
        'circle-stroke-color': ['get', 'color'],
        'circle-stroke-opacity': 0,
        'circle-opacity': ['interpolate', ['linear'], ['zoom'],
          ZOOM.CITY_MIN, 0,
          ZOOM.CITY_MAX, 1,
        ],
      },
    });

    /* ─── 3. Main dot — bigger, bold category color ─── */
    safeLayer({
      id: 'project-points',
      type: 'circle',
      source: 'projects',
      filter: ['!', ['has', 'point_count']],
      minzoom: 11,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'],
          ZOOM.CITY_MAX,    ['case', ['==', ['get', 'is_featured'], 1], 6, 5],
          ZOOM.DENSE_MIN,   ['case', ['==', ['get', 'is_featured'], 1], 8, 6.5],
          ZOOM.PROJECT_MIN, ['case', ['==', ['get', 'is_featured'], 1], 11, 9],
          ZOOM.LOCAL_MIN,   ['case', ['==', ['get', 'is_featured'], 1], 14, 11],
        ],
        'circle-color': ['get', 'color'],
        'circle-stroke-width': ['interpolate', ['linear'], ['zoom'],
          ZOOM.CITY_MAX, 1.5,
          ZOOM.PROJECT_MIN, 2,
          ZOOM.LOCAL_MIN, 2.5,
        ],
        'circle-stroke-color': ['get', 'stroke'],
        'circle-opacity': ['interpolate', ['linear'], ['zoom'],
          ZOOM.CITY_MIN, 0,
          ZOOM.CITY_MAX, 0.9,
          ZOOM.DENSE_MIN, 1,
        ],
        'circle-stroke-opacity': ['interpolate', ['linear'], ['zoom'],
          ZOOM.CITY_MIN, 0,
          ZOOM.CITY_MAX, 0.9,
          ZOOM.DENSE_MIN, 1,
        ],
      },
    });

    /* ─── 4. Inner highlight — white center for depth ─── */
    safeLayer({
      id: 'project-points-center',
      type: 'circle',
      source: 'projects',
      filter: ['!', ['has', 'point_count']],
      minzoom: 11,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'],
          ZOOM.DENSE_MIN, 2,
          ZOOM.PROJECT_MIN, 3.5,
          ZOOM.LOCAL_MIN, 5,
        ],
        'circle-color': '#fff',
        'circle-opacity': ['interpolate', ['linear'], ['zoom'],
          ZOOM.CITY_MAX, 0,
          ZOOM.DENSE_MIN, 0.75,
        ],
      },
    });

    /* ─── 5. Click hitarea for individual projects (invisible, LARGE for touch) ─── */
    safeLayer({
      id: 'project-points-hitarea',
      type: 'circle',
      source: 'projects',
      filter: ['!', ['has', 'point_count']],
      minzoom: 11,
      paint: {
        'circle-color': 'rgba(0, 0, 0, 0.01)',
        'circle-radius': ['interpolate', ['linear'], ['zoom'],
          ZOOM.CITY_MAX, 24,
          ZOOM.DENSE_MIN, 30,
          ZOOM.PROJECT_MIN, 36,
        ],
      },
    });

    /* ─── 6. Featured ring (gold double outline) ─── */
    safeLayer({
      id: 'project-featured-dot',
      type: 'circle',
      source: 'projects',
      filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'is_featured'], 1]],
      minzoom: 11,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'],
          ZOOM.CITY_MAX, 9,
          ZOOM.DENSE_MIN, 12,
          ZOOM.PROJECT_MIN, 16,
          ZOOM.LOCAL_MIN, 20,
        ],
        'circle-color': 'transparent',
        'circle-stroke-width': ['interpolate', ['linear'], ['zoom'],
          ZOOM.CITY_MAX, 1.5,
          ZOOM.PROJECT_MIN, 2,
          ZOOM.LOCAL_MIN, 2.5,
        ],
        'circle-stroke-color': 'rgba(214, 168, 79, 0.6)',
        'circle-opacity': ['interpolate', ['linear'], ['zoom'],
          ZOOM.CITY_MAX, 0,
          ZOOM.DENSE_MIN, 0.8,
          ZOOM.PROJECT_MIN, 1,
        ],
        'circle-stroke-opacity': ['interpolate', ['linear'], ['zoom'],
          ZOOM.CITY_MAX, 0,
          ZOOM.DENSE_MIN, 0.8,
          ZOOM.PROJECT_MIN, 1,
        ],
      },
    });

    /* ─── 7. Hover ring ─── */
    safeLayer({
      id: 'project-points-hover',
      type: 'circle',
      source: 'projects',
      filter: ['==', ['get', 'id'], ''],
      minzoom: 11,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'],
          ZOOM.CITY_MAX, 14,
          ZOOM.DENSE_MIN, 20,
          ZOOM.PROJECT_MIN, 26,
        ],
        'circle-color': 'transparent',
        'circle-stroke-width': 2,
        'circle-stroke-color': 'rgba(255, 255, 255, 0.3)',
      },
    });

    /* ─── 8. Category label at high zoom ─── */
    safeLayer({
      id: 'project-category-label',
      type: 'symbol',
      source: 'projects',
      filter: ['!', ['has', 'point_count']],
      minzoom: 11,
      layout: {
        'text-field': ['get', 'category_name'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 13, 9, 16, 11],
        'text-offset': [0, 2.2],
        'text-anchor': 'top',
        'text-font': ['Noto Sans Regular'],
        'text-max-width': 8,
        'text-allow-overlap': false,
        'text-optional': true,
      },
      paint: {
        'text-color': isLight ? '#2D3A2E' : '#e8e4dc',
        'text-halo-color': isLight ? 'rgba(255,255,255,0.85)' : 'rgba(4,10,20,0.7)',
        'text-halo-width': 1,
        'text-opacity': ['interpolate', ['linear'], ['zoom'], 13, 0, 14, 0.85],
      },
    });

    /* ═══ PULSE ANIMATION (THROTTLED for touch screen performance) ═══ */
    let pulsePhase = 0;
    const PULSE_SPEED = 0.018;
    let lastPulseUpdate = 0;
    const PULSE_THROTTLE = 80; // ~12fps — smooth enough visually, saves GPU
    function animatePulse(ts) {
      if (ts - lastPulseUpdate < PULSE_THROTTLE) {
        map._namaa_pulseId = requestAnimationFrame(animatePulse);
        return;
      }
      lastPulseUpdate = ts;
      pulsePhase = (pulsePhase + PULSE_SPEED * (PULSE_THROTTLE / 16.67)) % 1;
      if (!map.getLayer('project-points-pulse')) return;
      // Skip pulse animation at low zoom (region clusters territory)
      if (map.getZoom() < 7) {
        map._namaa_pulseId = requestAnimationFrame(animatePulse);
        return;
      }
      const expand = pulsePhase;
      const baseR = 10;
      const maxR = 22;
      const r = baseR + expand * (maxR - baseR);
      const opacity = 0.55 * (1 - expand);
      try {
        map.setPaintProperty('project-points-pulse', 'circle-radius',
          ['interpolate', ['linear'], ['zoom'],
            ZOOM.CITY_MAX, r * 0.6,
            ZOOM.PROJECT_MIN, r,
            ZOOM.LOCAL_MIN, r * 1.3,
          ]);
        map.setPaintProperty('project-points-pulse', 'circle-stroke-opacity', opacity);
      } catch (_) {}
      map._namaa_pulseId = requestAnimationFrame(animatePulse);
    }
    map._namaa_pulseId = requestAnimationFrame(animatePulse);

    /* ═══ ANIMATION LOCK — prevents stacking clicks during transitions ═══ */
    let _animLocked = false;
    function lockAnimation(durationMs) {
      _animLocked = true;
      setTimeout(() => { _animLocked = false; }, durationMs + 50);
    }
    // Also listen for moveend to release lock early if animation finishes
    map.on('moveend', () => { _animLocked = false; });

    /* ═══ CLICK HANDLERS ═══ */

    /* ─── Cluster count text (must exist before click registration) ─── */
    safeLayer({
      id: 'project-clusters-text',
      type: 'symbol',
      source: 'projects',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': ['Noto Sans Regular'],
        'text-size': ['step', ['get', 'point_count'],
          13, 10, 15, 50, 17,
        ],
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': '#ffffff',
        'text-opacity': ['interpolate', ['linear'], ['zoom'], 10, 0, 11, 0.95],
      },
    });

    /* ═══ PRIMARY CITY HUBS (Level 1 — the only initial view) ═══ */
    safeSource('region-clusters', {
      type: 'geojson',
      data: hubClustersGeo,
    });

    /* Hub glow — dynamic visibility: big hubs + all regional reps always visible */
    const hubVisFilter = ['all',
      ['>', ['get', 'count'], 0],
      ['any',
        ['==', ['get', 'is_regional'], 1],
        ['>=', ['get', 'count'], ['interpolate', ['linear'], ['zoom'], 4, 120, 7, 30, 9, 1]],
      ],
    ];
    safeLayer({
      id: 'region-clusters-glow', type: 'circle', source: 'region-clusters',
      maxzoom: 11,
      filter: hubVisFilter,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['get', 'count'], 20, 12, 100, 18, 500, 24, 2000, 32, 5000, 42],
        'circle-color': 'rgba(47, 174, 143, 0.08)',
        'circle-blur': 0.5,
        'circle-opacity': ['interpolate', ['linear'], ['zoom'], 4, 0.7, 9.5, 0.7, 11, 0],
      },
    });

    /* Hub circle */
    safeLayer({
      id: 'region-clusters-circle', type: 'circle', source: 'region-clusters',
      maxzoom: 11,
      filter: hubVisFilter,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['get', 'count'], 20, 7, 100, 11, 500, 16, 2000, 22, 5000, 30],
        'circle-color': 'rgba(47, 174, 143, 0.9)',
        'circle-stroke-width': 2,
        'circle-stroke-color': 'rgba(47, 174, 143, 0.3)',
        'circle-opacity': ['interpolate', ['linear'], ['zoom'], 4, 1, 9.5, 1, 11, 0],
        'circle-stroke-opacity': ['interpolate', ['linear'], ['zoom'], 4, 1, 9.5, 1, 11, 0],
      },
    });

    /* Hub hitarea */
    safeLayer({
      id: 'region-clusters-hitarea', type: 'circle', source: 'region-clusters',
      maxzoom: 11,
      filter: hubVisFilter,
      paint: {
        'circle-color': 'rgba(0, 0, 0, 0.01)',
        'circle-radius': ['interpolate', ['linear'], ['get', 'count'], 20, 18, 100, 26, 500, 36, 2000, 44, 5000, 54],
      },
    });

    /* Hub count — hide numbers for tiny hubs at far zoom */
    safeLayer({
      id: 'region-clusters-count', type: 'symbol', source: 'region-clusters',
      maxzoom: 11,
      filter: hubVisFilter,
      layout: {
        'text-field': ['to-string', ['get', 'count']],
        'text-font': ['Noto Sans Bold'],
        'text-size': ['interpolate', ['linear'], ['get', 'count'], 20, 9, 200, 12, 2000, 16],
        'text-allow-overlap': false,
        'text-ignore-placement': false,
        'text-offset': [0, -0.1],
      },
      paint: {
        'text-color': '#ffffff',
        'text-opacity': ['interpolate', ['linear'], ['zoom'], 4, 1, 9.5, 1, 11, 0],
      },
    });

    /* Hub label — show ALL hub names including label-only anchors */
    const hubLabelFilter = ['any',
      ['==', ['get', 'is_regional'], 1],
      ['>=', ['get', 'count'], ['interpolate', ['linear'], ['zoom'], 4, 120, 7, 30, 9, 1]],
      ['==', ['get', 'label_only'], 1],
    ];
    safeLayer({
      id: 'region-clusters-label', type: 'symbol', source: 'region-clusters',
      maxzoom: 11,
      filter: hubLabelFilter,
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Regular'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 4, 8, 8, 11],
        'text-offset': ['case', ['==', ['get', 'label_only'], 1], ['literal', [0, 0]], ['literal', [0, 1.8]]],
        'text-anchor': ['case', ['==', ['get', 'label_only'], 1], 'center', 'top'],
        'text-allow-overlap': false,
        'text-optional': true,
        'text-max-width': 8,
      },
      paint: {
        'text-color': 'rgba(245, 241, 232, 0.75)',
        'text-halo-color': 'rgba(4, 10, 20, 0.7)',
        'text-halo-width': 1.5,
        'text-opacity': ['interpolate', ['linear'], ['zoom'], 4, 0.8, 9.5, 0.8, 11, 0],
      },
    });

    /* ═══ CITY & DISTRICT CLUSTERS — HIDDEN (replaced by Hub-based system) ═══ */
    safeSource('city-clusters', { type: 'geojson', data: cityClustersGeo });
    safeLayer({ id: 'city-clusters-glow', type: 'circle', source: 'city-clusters', paint: { 'circle-radius': 1, 'circle-opacity': 0 } });
    safeLayer({ id: 'city-clusters-circle', type: 'circle', source: 'city-clusters', paint: { 'circle-radius': 1, 'circle-opacity': 0 } });
    safeLayer({ id: 'city-clusters-hitarea', type: 'circle', source: 'city-clusters', paint: { 'circle-radius': 1, 'circle-color': 'rgba(0,0,0,0)' } });
    safeLayer({ id: 'city-clusters-count', type: 'symbol', source: 'city-clusters', layout: { 'text-field': '', 'text-font': ['Noto Sans Bold'], 'text-size': 1 }, paint: { 'text-opacity': 0 } });
    safeLayer({ id: 'city-clusters-label', type: 'symbol', source: 'city-clusters', layout: { 'text-field': '', 'text-font': ['Noto Sans Regular'], 'text-size': 1 }, paint: { 'text-opacity': 0 } });

    safeSource('district-clusters', { type: 'geojson', data: districtClustersGeo });
    safeLayer({ id: 'district-clusters-glow', type: 'circle', source: 'district-clusters', paint: { 'circle-radius': 1, 'circle-opacity': 0 } });
    safeLayer({ id: 'district-clusters-circle', type: 'circle', source: 'district-clusters', paint: { 'circle-radius': 1, 'circle-opacity': 0 } });
    safeLayer({ id: 'district-clusters-hitarea', type: 'circle', source: 'district-clusters', paint: { 'circle-radius': 1, 'circle-color': 'rgba(0,0,0,0)' } });
    safeLayer({ id: 'district-clusters-count', type: 'symbol', source: 'district-clusters', layout: { 'text-field': '', 'text-font': ['Noto Sans Bold'], 'text-size': 1 }, paint: { 'text-opacity': 0 } });
    safeLayer({ id: 'district-clusters-label', type: 'symbol', source: 'district-clusters', layout: { 'text-field': '', 'text-font': ['Noto Sans Regular'], 'text-size': 1 }, paint: { 'text-opacity': 0 } });

    /* ═══ FEATURED PROJECTS — separate unclustered layer, always on top ═══ */
    safeSource('featured-projects', {
      type: 'geojson',
      data: featuredGeojson,
    });

    /* Featured: outer glow ring — LARGE, visible from zoom 7 */
    safeLayer({
      id: 'featured-glow', type: 'circle', source: 'featured-projects',
      minzoom: 7,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 7, 14, 10, 24, 13, 32, 16, 40],
        'circle-color': 'rgba(214, 168, 79, 0.12)',
        'circle-blur': 0.5,
        'circle-opacity': ['interpolate', ['linear'], ['zoom'], 7, 0, 7.5, 0.4, 10, 0.6, 13, 0.7],
      },
    });

    /* Featured: main dot — gold, MUCH larger than regular */
    safeLayer({
      id: 'featured-dot', type: 'circle', source: 'featured-projects',
      minzoom: 7,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 7, 6, 10, 12, 13, 16, 16, 20],
        'circle-color': '#D6A84F',
        'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 7, 2, 13, 3],
        'circle-stroke-color': 'rgba(214, 168, 79, 0.35)',
        'circle-opacity': ['interpolate', ['linear'], ['zoom'], 7, 0, 7.5, 0.85, 10, 1],
        'circle-stroke-opacity': ['interpolate', ['linear'], ['zoom'], 7, 0, 7.5, 0.7, 10, 1],
      },
    });

    /* Featured: white center highlight */
    safeLayer({
      id: 'featured-center', type: 'circle', source: 'featured-projects',
      minzoom: 7,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 7, 2.5, 10, 4.5, 13, 6, 16, 8],
        'circle-color': '#ffffff',
        'circle-opacity': ['interpolate', ['linear'], ['zoom'], 7, 0, 8, 0.6, 10, 0.8],
      },
    });

    /* Featured: click hitarea — EXTRA LARGE for easy tapping */
    safeLayer({
      id: 'featured-hitarea', type: 'circle', source: 'featured-projects',
      minzoom: 7,
      paint: {
        'circle-color': 'rgba(0, 0, 0, 0.01)',
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 7, 24, 10, 40, 13, 50, 16, 60],
      },
    });

    /* Featured: name label */
    safeLayer({
      id: 'featured-label', type: 'symbol', source: 'featured-projects',
      minzoom: 9,
      layout: {
        'text-field': isArabic ? ['get', 'project_name_ar'] : ['coalesce', ['get', 'project_name_en'], ['get', 'project_name_ar']],
        'text-font': ['Noto Sans Regular'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 10, 8, 14, 11],
        'text-offset': [0, 2],
        'text-anchor': 'top',
        'text-allow-overlap': false,
        'text-optional': true,
        'text-max-width': 8,
      },
      paint: {
        'text-color': 'rgba(214, 168, 79, 0.9)',
        'text-halo-color': isLight ? 'rgba(255,255,255,0.85)' : 'rgba(4, 10, 20, 0.7)',
        'text-halo-width': 1,
        'text-opacity': ['interpolate', ['linear'], ['zoom'], 10, 0, 11, 0.7, 14, 0.9],
      },
    });

    /* ═══ LAYER ORDERING — Move cluster layers above region polygons ═══ */
    const reorderLayers = () => {
      const order = [
        'region-clusters-glow', 'region-clusters-circle', 'region-clusters-hitarea',
        'region-clusters-count', 'region-clusters-label',
        'city-clusters-glow', 'city-clusters-circle', 'city-clusters-hitarea',
        'city-clusters-count', 'city-clusters-label',
        'district-clusters-glow', 'district-clusters-circle', 'district-clusters-hitarea',
        'district-clusters-count', 'district-clusters-label',
        'project-clusters', 'project-clusters-hitarea', 'project-clusters-text',
        'project-points-glow', 'project-points-pulse', 'project-points',
        'project-points-center', 'project-points-hitarea', 'project-featured-dot',
        'project-points-hover', 'project-category-label',
        // Featured projects always on top
        'featured-glow', 'featured-dot', 'featured-center', 'featured-hitarea', 'featured-label',
      ];
      order.forEach(id => { if (map.getLayer(id)) try { map.moveLayer(id); } catch(e){} });
    };
    reorderLayers();
    // Reorder after idle (all layers loaded, including RegionPolygons)
    const idleHandler = () => { reorderLayers(); map.off('idle', idleHandler); };
    map.on('idle', idleHandler);
    // Also reorder when source data changes (ensures clusters always on top)
    const sourceHandler = (e) => {
      if (e.sourceId === 'regions' || e.sourceId === 'region-labels') reorderLayers();
    };
    map.on('sourcedata', sourceHandler);
    setTimeout(reorderLayers, 200);
    setTimeout(reorderLayers, 1000);
    setTimeout(reorderLayers, 3000);

    // Helper: popup for overlapping projects
    function showNearbyPopup(features, lngLat) {
      if (popupRef.current) popupRef.current.remove();
      const popupText = isLight ? 'rgba(50,45,35,0.8)' : 'rgba(245,241,232,0.8)';
      const popupBorder = isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)';

      const listHtml = features.map(f => {
        const p = f.properties || f;
        const name = isArabic ? p.project_name_ar : (p.project_name_en || p.project_name_ar);
        return `<div class="v2-overlap-item" data-id="${p.id}" style="cursor:pointer;padding:6px 10px;border-bottom:1px solid ${popupBorder};transition:background 0.15s;">
          <span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${p.color || '#2FAE8F'};margin-inline-end:6px;"></span>
          <span style="color:${popupText};font-size:12px;">${name}</span>
        </div>`;
      }).join('');

      const popup = new maplibregl.Popup({
        closeButton: true, closeOnClick: true,
        className: `v2-overlap-popup${isLight ? ' v2-popup-light' : ''}`,
        maxWidth: '260px',
      })
        .setLngLat(lngLat)
        .setHTML(`<div style="direction:rtl;font-family:'Cairo',sans-serif;">${listHtml}</div>`)
        .addTo(map);
      popupRef.current = popup;

      setTimeout(() => {
        document.querySelectorAll('.v2-overlap-item').forEach(item => {
          item.addEventListener('click', () => {
            const id = item.dataset.id;
            const feat = features.find(f => (f.properties || f).id === id);
            if (feat) focusSingleProject(feat.properties || feat, lngLat);
            popup.remove();
          });
        });
      }, 50);
    }

    // ═══ Project focus — smooth zoom + center using easeTo ═══
    let flyMoveEndHandler = null;
    function focusSingleProject(props, coords) {
      if (popupRef.current) popupRef.current.remove();
      if (flyMoveEndHandler) { map.off('moveend', flyMoveEndHandler); flyMoveEndHandler = null; }

      if (onProjectSelect) {
        const enriched = enrichProject(props, coords);
        onProjectSelect(enriched);
      }

      const currentZoom = map.getZoom();
      const targetZoom = currentZoom >= ZOOM.PROJECT_MIN
        ? Math.min(currentZoom + 0.5, ZOOM.MAX)
        : CAMERA.PROJECT_FOCUS_ZOOM;

      const duration = CAMERA.PROJECT_FOCUS_DURATION;
      lockAnimation(duration);

      map.easeTo({
        center: coords,
        zoom: targetZoom,
        duration: duration,
        easing: CAMERA.EASE_OUT_QUINT,
        offset: CAMERA.PREVIEW_OFFSET,
      });

      map.setFilter('project-points-hover', ['==', ['get', 'id'], props.id]);
    }

    // ═══ Drill-down helper: zoom into clicked cluster point ═══
    // IMPORTANT: Always centers on clusterCoords (where user clicked), NOT on bounds center
    function drillToBounds(props, clusterCoords, maxZoom, minZoom, duration) {
      const minLng = Number(props.minLng);
      const maxLng = Number(props.maxLng);
      const minLat = Number(props.minLat);
      const maxLat = Number(props.maxLat);
      const lngSpan = Math.abs(maxLng - minLng);
      const latSpan = Math.abs(maxLat - minLat);

      lockAnimation(duration + 200);

      // If bounds are too tight, use easeTo directly
      if (lngSpan < 0.02 && latSpan < 0.02) {
        map.easeTo({
          center: clusterCoords,
          zoom: maxZoom,
          duration: duration,
          easing: CAMERA.EASE_IN_OUT_QUART,
        });
        return;
      }

      // Use cameraForBounds ONLY to compute the ideal zoom level
      const bounds = [[minLng, minLat], [maxLng, maxLat]];
      const padding = { top: 100, bottom: 100, left: 100, right: 100 };
      const camera = map.cameraForBounds(bounds, { padding, maxZoom });

      const targetZoom = camera
        ? Math.min(Math.max(camera.zoom, minZoom), maxZoom)
        : minZoom;

      // Center on the CLUSTER POINT (where user clicked), not the bounds center
      map.easeTo({
        center: clusterCoords,
        zoom: targetZoom,
        duration: duration,
        easing: CAMERA.EASE_IN_OUT_QUART,
      });
    }

    // ═══ SINGLE UNIFIED CLICK HANDLER — Featured → Hub → Local Cluster → Project ═══
    map.on('click', (e) => {
      if (_animLocked) return;
      if (e.originalEvent._v2handled) return;
      // Block ALL map click interactions during Showcase Mode
      if (presentationModeRef.current) return;

      const point = e.point;

      // ─── PRIORITY: Featured projects — zoom + open detail panel ───
      const featHitLayers = ['featured-hitarea', 'featured-dot'].filter(id => map.getLayer(id));
      if (featHitLayers.length) {
        const featHits = map.queryRenderedFeatures(point, { layers: featHitLayers });
        if (featHits.length > 0) {
          e.originalEvent._v2handled = true;
          const feat = featHits[0];
          focusSingleProject(feat.properties, feat.geometry.coordinates.slice());
          return;
        }
      }

      // ─── Level 1: Primary Hub click → zoom into hub area ───
      const hubHitLayers = ['region-clusters-hitarea', 'region-clusters-circle', 'region-clusters-count'].filter(id => map.getLayer(id));
      if (hubHitLayers.length) {
        const hits = map.queryRenderedFeatures(
          [[point.x - 24, point.y - 24], [point.x + 24, point.y + 24]],
          { layers: hubHitLayers }
        );
        const seen = new Set();
        const unique = hits.filter(h => {
          const hn = h.properties?.hub_name;
          if (!hn || seen.has(hn)) return false;
          seen.add(hn);
          return true;
        });
        if (unique.length) {
          let closest = unique[0];
          if (unique.length > 1) {
            let minDist = Infinity;
            unique.forEach(h => {
              const projected = map.project(h.geometry.coordinates);
              const dx = projected.x - point.x;
              const dy = projected.y - point.y;
              const d = dx * dx + dy * dy;
              if (d < minDist) { minDist = d; closest = h; }
            });
          }
          e.originalEvent._v2handled = true;
          const coords = closest.geometry.coordinates.slice();
          const count = closest.properties.count || 0;
          // Smart zoom: fewer projects → zoom deeper, many → zoom to local clusters
          const targetZoom = count < 30 ? 14 : count < 200 ? 12.5 : count < 1000 ? 11.5 : 11;
          const duration = CAMERA.CLUSTER_EASE_DURATION;
          lockAnimation(duration + 200);
          map.easeTo({
            center: coords,
            zoom: targetZoom,
            duration: duration,
            easing: CAMERA.EASE_IN_OUT_QUART,
          });
          return;
        }
      }

      // ─── Level 2: MapLibre native clusters → smart bounds zoom ───
      const clusterLayers = ['project-clusters', 'project-clusters-hitarea', 'project-clusters-text'].filter(id => map.getLayer(id));
      if (clusterLayers.length) {
        const clusterHits = map.queryRenderedFeatures(point, { layers: clusterLayers });
        const clusterFeature = clusterHits.find(f => f.properties?.cluster_id != null);
        if (clusterFeature) {
          e.originalEvent._v2handled = true;
          const clusterId = clusterFeature.properties.cluster_id;
          const clusterCoords = clusterFeature.geometry.coordinates.slice();
          const pointCount = clusterFeature.properties.point_count || 0;
          const src = map.getSource('projects');

          // Get actual child points to compute smart bounds
          const leafCount = Math.min(pointCount, 200);
          src.getClusterLeaves(clusterId, leafCount, 0).then(leaves => {
            if (!leaves || leaves.length === 0) {
              // Fallback: use expansion zoom
              src.getClusterExpansionZoom(clusterId).then(ez => {
                lockAnimation(CAMERA.CLUSTER_EASE_DURATION);
                map.easeTo({ center: clusterCoords, zoom: Math.min(ez, ZOOM.MAX), duration: CAMERA.CLUSTER_EASE_DURATION, easing: CAMERA.EASE_IN_OUT_QUART });
              });
              return;
            }

            // Compute bounds from actual child points
            const bounds = new maplibregl.LngLatBounds();
            leaves.forEach(leaf => bounds.extend(leaf.geometry.coordinates));

            // Check if bounds are meaningful (not a single point)
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            const spanLng = Math.abs(ne.lng - sw.lng);
            const spanLat = Math.abs(ne.lat - sw.lat);

            const duration = CAMERA.CLUSTER_EASE_DURATION;
            lockAnimation(duration + 200);

            if (spanLng < 0.005 && spanLat < 0.005) {
              // All points basically at same spot — zoom to fixed level
              map.easeTo({ center: clusterCoords, zoom: Math.min(map.getZoom() + 3, 15), duration, easing: CAMERA.EASE_IN_OUT_QUART });
            } else {
              // Fit to actual child bounds with smart padding
              map.fitBounds(bounds, { padding: { top: 80, bottom: 80, left: 80, right: 80 }, maxZoom: 16, duration, easing: CAMERA.EASE_IN_OUT_QUART });
            }
          }).catch(() => {
            // Fallback on error
            src.getClusterExpansionZoom(clusterId).then(ez => {
              lockAnimation(CAMERA.CLUSTER_EASE_DURATION);
              map.easeTo({ center: clusterCoords, zoom: Math.min(ez, ZOOM.MAX), duration: CAMERA.CLUSTER_EASE_DURATION, easing: CAMERA.EASE_IN_OUT_QUART });
            });
          });
          return;
        }
      }
    });

    // Hub hover
    ['region-clusters-circle', 'region-clusters-hitarea', 'region-clusters-count'].forEach(layerId => {
      map.on('mouseenter', layerId, () => {
        if (map.getLayer('region-clusters-circle')) {
          map.setPaintProperty('region-clusters-circle', 'circle-stroke-width', 3);
        }
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', layerId, () => {
        if (map.getLayer('region-clusters-circle')) {
          map.setPaintProperty('region-clusters-circle', 'circle-stroke-width', 2);
        }
        map.getCanvas().style.cursor = '';
      });
    });

    // ═══ LAYER-SPECIFIC CLICK HANDLERS ═══

    // Hub click (Level 1 → zoom into hub area)
    ['region-clusters-hitarea', 'region-clusters-circle', 'region-clusters-count'].forEach(layerId => {
      if (map.getLayer(layerId)) {
        map.on('click', layerId, (e) => {
          if (_animLocked || e.originalEvent._v2handled) return;
          const feature = e.features?.[0];
          if (!feature?.properties?.hub_name) return;
          e.originalEvent._v2handled = true;

          const hubName = feature.properties.hub_name;
          const count = feature.properties.count || 0;
          const duration = CAMERA.CLUSTER_EASE_DURATION;

          // Always use the PRIMARY_HUB city center as zoom target
          const hub = PRIMARY_HUBS.find(h => h.name === hubName);
          const center = hub ? [hub.lng, hub.lat] : feature.geometry.coordinates.slice();
          const targetZoom = count < 30 ? 14 : count < 200 ? 12.5 : count < 1000 ? 11.5 : 11;

          lockAnimation(duration + 200);
          map.easeTo({ center, zoom: targetZoom, duration, easing: CAMERA.EASE_IN_OUT_QUART });
        });
      }
    });

    // ═══ PROJECT CLICK — Layer-specific ═══
    function handleProjectClick(e) {
      if (_animLocked) return;
      if (e.originalEvent._v2handled) return;

      const feature = e.features?.[0];
      if (!feature?.properties?.id) return;
      // Ignore cluster features that may leak through
      if (feature.properties.cluster_id) return;

      e.originalEvent._v2handled = true;

      const coords = feature.geometry.coordinates.slice();
      const props = feature.properties;
      const point = map.project(coords);

      // When overlapping projects found — zoom deeper, NEVER show a list
      const currentZoom = map.getZoom();
      if (currentZoom >= ZOOM.DENSE_MIN) {
        const nearby = map.queryRenderedFeatures(
          [[point.x - 18, point.y - 18], [point.x + 18, point.y + 18]],
          { layers: ['project-points'] }
        );
        const seen = new Set();
        const unique = nearby.filter(f => {
          const id = f.properties.id;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });
        if (unique.length > 1) {
          // Zoom deeper on the same spot instead of showing a list
          const targetZoom = Math.min(currentZoom + 2, ZOOM.MAX);
          const duration = CAMERA.CLUSTER_EASE_DURATION;
          lockAnimation(duration);
          map.easeTo({
            center: coords,
            zoom: targetZoom,
            duration: duration,
            easing: CAMERA.EASE_IN_OUT_QUART,
          });
          return;
        }
      }

      focusSingleProject(props, coords);
    }

    ['project-points', 'project-points-hitarea'].forEach(layerId => {
      if (map.getLayer(layerId)) {
        map.on('click', layerId, handleProjectClick);
      }
    });

    // ═══ FEATURED PROJECT CLICK — zoom + open detail panel together ═══
    ['featured-hitarea', 'featured-dot'].forEach(layerId => {
      if (map.getLayer(layerId)) {
        map.on('click', layerId, (e) => {
          if (_animLocked || e.originalEvent._v2handled) return;
          const feature = e.features?.[0];
          if (!feature?.properties?.id) return;
          e.originalEvent._v2handled = true;
          focusSingleProject(feature.properties, feature.geometry.coordinates.slice());
        });
      }
    });

    // Featured hover
    ['featured-dot', 'featured-hitarea'].forEach(layerId => {
      map.on('mouseenter', layerId, () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', layerId, () => { map.getCanvas().style.cursor = ''; });
    });

    /* ═══ Hover feedback ═══ */
    let hoveredProjectId = null;
    map.on('mousemove', 'project-points', (e) => {
      const id = e.features[0]?.properties?.id;
      if (id && id !== hoveredProjectId) {
        hoveredProjectId = id;
        map.setFilter('project-points-hover', ['==', ['get', 'id'], id]);
        map.getCanvas().style.cursor = 'pointer';
      }
    });
    map.on('mouseleave', 'project-points', () => {
      hoveredProjectId = null;
      map.setFilter('project-points-hover', ['==', ['get', 'id'], '']);
      map.getCanvas().style.cursor = '';
    });

    // Cluster hover — clean highlight
    const clusterHoverLayers = ['project-clusters', 'project-clusters-hitarea', 'project-clusters-text'];
    clusterHoverLayers.forEach(layerId => {
      map.on('mouseenter', layerId, () => {
        map.setPaintProperty('project-clusters', 'circle-stroke-width', 2.5);
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', layerId, () => {
        map.setPaintProperty('project-clusters', 'circle-stroke-width', 1.5);
        map.getCanvas().style.cursor = '';
      });
    });

  }, [map, loaded, geojson, enrichProject, onProjectSelect, isArabic, isLight]);

  /* ── Cleanup pulse animation on unmount ── */
  useEffect(() => {
    return () => {
      if (map?._namaa_pulseId) {
        cancelAnimationFrame(map._namaa_pulseId);
        map._namaa_pulseId = null;
      }
    };
  }, [map]);

  /* ── Update source data when filters change ── */
  useEffect(() => {
    if (!map || !loaded || !layersAddedRef.current) return;
    const src = map.getSource('projects');
    if (src) src.setData(geojson);
    const featSrc = map.getSource('featured-projects');
    if (featSrc) featSrc.setData(featuredGeojson);
    const hubSrc = map.getSource('region-clusters');
    if (hubSrc) hubSrc.setData(hubClustersGeo);
    const citySrc = map.getSource('city-clusters');
    if (citySrc) citySrc.setData(cityClustersGeo);
    const distSrc = map.getSource('district-clusters');
    if (distSrc) distSrc.setData(districtClustersGeo);
  }, [map, loaded, geojson, featuredGeojson, hubClustersGeo, cityClustersGeo, districtClustersGeo]);

  /* ═══ SUCCESS STORIES LAYER ═══ */
  useEffect(() => {
    if (!map || !loaded) return;

    // Add source once
    if (!storyLayerAddedRef.current && !map.getSource('success-stories-src')) {
      storyLayerAddedRef.current = true;
      map.addSource('success-stories-src', { type: 'geojson', data: storiesGeojson });

      // Glow
      safeLayer({
        id: 'story-points-glow', type: 'circle', source: 'success-stories-src',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 12, 10, 18, 14, 24],
          'circle-color': '#E8A838', 'circle-opacity': 0.18, 'circle-blur': 0.8,
        },
      });
      // Main dot
      safeLayer({
        id: 'story-points', type: 'circle', source: 'success-stories-src',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 5, 10, 7, 14, 10],
          'circle-color': '#E8A838',
          'circle-stroke-width': 2, 'circle-stroke-color': '#C4841D',
          'circle-opacity': 1,
        },
      });
      // Center highlight
      safeLayer({
        id: 'story-points-center', type: 'circle', source: 'success-stories-src',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 14, 4],
          'circle-color': '#fff', 'circle-opacity': 0.7,
        },
      });
      // Hitarea
      safeLayer({
        id: 'story-points-hitarea', type: 'circle', source: 'success-stories-src',
        paint: { 'circle-color': 'rgba(0,0,0,0.01)', 'circle-radius': 30 },
      });
      // Label
      safeLayer({
        id: 'story-label', type: 'symbol', source: 'success-stories-src',
        layout: {
          'text-field': ['get', 'title_ar'],
          'text-font': ['Noto Sans Regular'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 8, 8, 14, 11],
          'text-offset': [0, 1.8], 'text-anchor': 'top',
          'text-allow-overlap': false, 'text-optional': true, 'text-max-width': 8,
        },
        paint: {
          'text-color': '#E8A838',
          'text-halo-color': isLight ? 'rgba(255,255,255,0.85)' : 'rgba(4,10,20,0.7)',
          'text-halo-width': 1,
        },
      });

      // Click handler for success story markers
      ['story-points', 'story-points-hitarea'].forEach(layerId => {
        map.on('click', layerId, (e) => {
          if (e.originalEvent._v2handled) return;
          const feature = e.features?.[0];
          if (!feature?.properties?.id) return;
          e.originalEvent._v2handled = true;

          const props = feature.properties;
          const coords = feature.geometry.coordinates.slice();

          if (onProjectSelect) {
            onProjectSelect({
              id: props.id,
              _type: 'success_story',
              title_ar: props.title_ar,
              title_en: props.title_en,
              city: props.city,
              region: props.region,
              sector: props.sector,
              short_hook: props.short_hook,
              cover_image: props.cover_image,
              latitude: coords[1],
              longitude: coords[0],
            });
          }

          map.easeTo({ center: coords, zoom: Math.min(map.getZoom() + 1, 16), duration: 600 });
        });
      });

      // Hover
      ['story-points', 'story-points-hitarea'].forEach(layerId => {
        map.on('mouseenter', layerId, () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', layerId, () => { map.getCanvas().style.cursor = ''; });
      });
    }

    // Update data
    const storySrc = map.getSource('success-stories-src');
    if (storySrc) storySrc.setData(storiesGeojson);

  }, [map, loaded, storiesGeojson, safeLayer, onProjectSelect, isLight]);

  /* ═══ OUTLETS LAYER ═══ */
  useEffect(() => {
    if (!map || !loaded) return;
    if (!outletsLayerAddedRef.current && !map.getSource('outlets-src')) {
      outletsLayerAddedRef.current = true;
      map.addSource('outlets-src', { type: 'geojson', data: outletsGeojson });
      safeLayer({ id: 'outlets-glow', type: 'circle', source: 'outlets-src', paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 10, 14, 20], 'circle-color': '#4A8AC0', 'circle-opacity': 0.15, 'circle-blur': 0.8 } });
      safeLayer({ id: 'outlets-points', type: 'circle', source: 'outlets-src', paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 5, 14, 9], 'circle-color': '#4A8AC0', 'circle-stroke-width': 2, 'circle-stroke-color': '#3570A0' } });
      safeLayer({ id: 'outlets-center', type: 'circle', source: 'outlets-src', paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 14, 3.5], 'circle-color': '#fff', 'circle-opacity': 0.7 } });
      safeLayer({ id: 'outlets-hitarea', type: 'circle', source: 'outlets-src', paint: { 'circle-color': 'rgba(0,0,0,0.01)', 'circle-radius': 28 } });
      safeLayer({ id: 'outlets-label', type: 'symbol', source: 'outlets-src', layout: { 'text-field': ['get', 'name'], 'text-font': ['Noto Sans Regular'], 'text-size': ['interpolate', ['linear'], ['zoom'], 8, 8, 14, 10], 'text-offset': [0, 1.6], 'text-anchor': 'top', 'text-allow-overlap': false, 'text-optional': true, 'text-max-width': 8 }, paint: { 'text-color': '#4A8AC0', 'text-halo-color': isLight ? 'rgba(255,255,255,0.85)' : 'rgba(4,10,20,0.7)', 'text-halo-width': 1 } });
      ['outlets-points', 'outlets-hitarea'].forEach(l => {
        map.on('mouseenter', l, () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', l, () => { map.getCanvas().style.cursor = ''; });
      });
    }
    const src = map.getSource('outlets-src');
    if (src) src.setData(outletsGeojson);
  }, [map, loaded, outletsGeojson, safeLayer, isLight]);

  /* ═══ JADA 30 LAYER ═══ */
  useEffect(() => {
    if (!map || !loaded) return;
    if (!jadaLayerAddedRef.current && !map.getSource('jada30-src')) {
      jadaLayerAddedRef.current = true;
      map.addSource('jada30-src', { type: 'geojson', data: jadaGeojson });
      safeLayer({ id: 'jada30-glow', type: 'circle', source: 'jada30-src', paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 12, 14, 22], 'circle-color': '#D6A84F', 'circle-opacity': 0.15, 'circle-blur': 0.8 } });
      safeLayer({ id: 'jada30-points', type: 'circle', source: 'jada30-src', paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 6, 14, 10], 'circle-color': '#D6A84F', 'circle-stroke-width': 2, 'circle-stroke-color': '#B6882F' } });
      safeLayer({ id: 'jada30-center', type: 'circle', source: 'jada30-src', paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2.5, 14, 4], 'circle-color': '#fff', 'circle-opacity': 0.7 } });
      safeLayer({ id: 'jada30-hitarea', type: 'circle', source: 'jada30-src', paint: { 'circle-color': 'rgba(0,0,0,0.01)', 'circle-radius': 28 } });
      safeLayer({ id: 'jada30-label', type: 'symbol', source: 'jada30-src', layout: { 'text-field': ['get', 'name'], 'text-font': ['Noto Sans Regular'], 'text-size': ['interpolate', ['linear'], ['zoom'], 8, 8, 14, 10], 'text-offset': [0, 1.6], 'text-anchor': 'top', 'text-allow-overlap': false, 'text-optional': true, 'text-max-width': 8 }, paint: { 'text-color': '#D6A84F', 'text-halo-color': isLight ? 'rgba(255,255,255,0.85)' : 'rgba(4,10,20,0.7)', 'text-halo-width': 1 } });
      ['jada30-points', 'jada30-hitarea'].forEach(l => {
        map.on('mouseenter', l, () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', l, () => { map.getCanvas().style.cursor = ''; });
      });
    }
    const src = map.getSource('jada30-src');
    if (src) src.setData(jadaGeojson);
  }, [map, loaded, jadaGeojson, safeLayer, isLight]);

  return null;
}
